// Myanmar Daily Prices - Frontend App (Burmese Old Newspaper Theme)

import { renderPriceCard } from './components/PriceCard';
import { renderSparkline } from './components/Sparkline';
import {
    renderShoppingCalculator,
    initCalculatorEvents,
    loadShoppingList,
    saveShoppingList,
    ShoppingItem
} from './components/ShoppingCalculator';
import { COMMODITIES, COMMODITY_CATEGORIES, getCommoditiesByCategory, formatValue } from './commodities';
import { t, Language, formatNewspaperDate } from './translations';
import { CONFIG } from './config';
import { fetchWithRetry, apiCache, isOnline, getErrorMessage } from './utils/apiUtils';
import { registerServiceWorker, setupInstallPrompt, promptInstall, isAppInstalled } from './utils/pwa';
import { loadPreferences, savePreferences, addFavorite, removeFavorite, isFavorite, checkPriceAlerts, markAlertTriggered, UserPreferences } from './utils/preferences';
import { renderPriceLeaderboard } from './utils/charts';

// State
const currentLanguage: Language = (() => {
    const stored = localStorage.getItem('mp-lang') as Language | null;
    return (stored && ['en', 'mm'].includes(stored)) ? stored : 'en';
})();

// Make language accessible for toggling (const limitation workaround)
(window as any).currentLanguage = currentLanguage;


interface PriceData {
    [key: string]: number | null | string | undefined;
    updated_at?: string;
}

interface LatestData {
    today: PriceData;
    yesterday: PriceData | null;
    deltas: Record<string, {
        value: number | null;
        change: number | null;
        percentChange: number | null;
        trend: 'up' | 'down' | 'stable' | 'unknown';
    }>;
}

interface TrendResponse {
    days: number;
    count: number;
    data: PriceData[];
}

let currentCategory = 'essentials';
let searchTerm = ''; // Search state
let shoppingList: ShoppingItem[] = loadShoppingList();
let calculatorOpen = false;
let userPreferences: UserPreferences = loadPreferences();

// Use API_BASE from config

async function fetchLatestData(category: string): Promise<LatestData> {
    const endpoint = category.startsWith('retail_') ? '/api/retail/latest' : '/api/latest';
    const cacheKey = `latest_${category}`;

    // Check cache first
    const cached = apiCache.get<LatestData>(cacheKey, CONFIG.CACHE_TTL_MS);
    if (cached) return cached;

    // Fetch with retry logic
    const data = await fetchWithRetry<LatestData>(
        `${CONFIG.API_BASE}${endpoint}`,
        {
            retries: CONFIG.API_RETRY_ATTEMPTS,
            retryDelay: CONFIG.API_RETRY_DELAY_MS,
            timeout: CONFIG.API_TIMEOUT_MS,
        }
    );

    // Cache the result
    apiCache.set(cacheKey, data);
    return data;
}

async function fetchTrendData(category: string, days: number = 7): Promise<TrendResponse> {
    const endpoint = category.startsWith('retail_') ? '/api/retail/trend' : '/api/trend';
    const cacheKey = `trend_${category}_${days}`;

    // Check cache first
    const cached = apiCache.get<TrendResponse>(cacheKey, CONFIG.CACHE_TTL_MS);
    if (cached) return cached;

    // Fetch with retry logic
    const data = await fetchWithRetry<TrendResponse>(
        `${CONFIG.API_BASE}${endpoint}?days=${days}`,
        {
            retries: CONFIG.API_RETRY_ATTEMPTS,
            retryDelay: CONFIG.API_RETRY_DELAY_MS,
            timeout: CONFIG.API_TIMEOUT_MS,
        }
    );

    // Cache the result
    apiCache.set(cacheKey, data);
    return data;
}

// Helper: Escape HTML to prevent XSS
function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// Toast Notification
function showToast(message: string, type: 'success' | 'error' | 'info' = 'success') {
    const container = document.getElementById('toast-container');
    if (!container) {
        console.warn('Toast container not found');
        return;
    }

    const toast = document.createElement('div');
    toast.className = `toast ${type}`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');

    // Build toast content safely
    const icon = document.createElement('span');
    icon.className = 'toast-icon';
    icon.textContent = type === 'success' ? '✔' : (type === 'error' ? '✖' : 'ℹ');

    const messageSpan = document.createElement('span');
    messageSpan.textContent = message; // Safe from XSS

    toast.appendChild(icon);
    toast.appendChild(messageSpan);
    container.appendChild(toast);

    // Animate in
    requestAnimationFrame(() => {
        toast.classList.add('visible');
    });

    // Remove after duration
    setTimeout(() => {
        toast.classList.remove('visible');
        setTimeout(() => {
            if (toast.parentNode) {
                container.removeChild(toast);
            }
        }, CONFIG.TOAST_FADE_DURATION);
    }, CONFIG.TOAST_DURATION);
}

// Tabs are no longer used in the newspaper layout (all sections shown at once)
function renderTabs() {
    // No-op: tabs removed in newspaper layout
}

function renderQuickStats(latestData: LatestData) {
    const quickStatsContainer = document.getElementById('quick-stats');
    if (!quickStatsContainer) return;

    // Target specific Essentials: USD, Gold, Fuel (92)
    const heroKeys = ['usd_sell', 'gold_24k', 'fuel_92_octane'];

    const heroItems = heroKeys.map(key => {
        const delta = latestData.deltas[key];
        const commodity = COMMODITIES.find(c => c.key === key);
        if (!delta || !commodity) return null;
        return { ...commodity, delta };
    }).filter(item => item !== null);

    if (heroItems.length === 0) {
        quickStatsContainer.innerHTML = '';
        return;
    }

    quickStatsContainer.innerHTML = heroItems.map(item => {
        if (!item) return '';
        const { delta, label } = item;

        const trendIcon = delta.trend === 'up' ? '▲' : (delta.trend === 'down' ? '▼' : '•');
        const trendClass = delta.trend === 'up' ? 'trend-up' : (delta.trend === 'down' ? 'trend-down' : 'trend-stable');
        const percent = delta.percentChange !== null ? `${Math.abs(delta.percentChange).toFixed(1)}%` : '-';

        // Shorten labels for hero
        let shortLabel = label;
        if (label.includes('USD')) shortLabel = 'USD (Sell)';
        if (label.includes('Gold')) shortLabel = 'Gold 24K';
        if (label.includes('Fuel')) shortLabel = '92 Octane';

        return `
            <div class="quick-stat">
                <span class="quick-stat-label">${shortLabel}</span>
                <span class="quick-stat-value" style="color: var(--${trendClass})">
                   ${trendIcon} ${percent}
                </span>
            </div>
        `;
    }).join('');
}

function injectAds() {
    // Inject AdSense <ins> tags into .ad-slot containers if they are empty
    // Only inject if the placeholder is visible (meaning no ad loaded yet)

    // Config from centralized file

    document.querySelectorAll('.ad-slot').forEach(slot => {
        if (slot.querySelector('ins.adsbygoogle')) return; // Already injected

        const slotId = (slot as HTMLElement).dataset.slotId || 'YOUR_AD_SLOT_ID';

        // Clear placeholder
        slot.innerHTML = '';

        const ins = document.createElement('ins');
        ins.className = 'adsbygoogle';
        ins.style.display = 'block';
        ins.setAttribute('data-ad-client', CONFIG.AD_CLIENT);
        ins.setAttribute('data-ad-slot', slotId);
        ins.setAttribute('data-ad-format', 'auto');
        ins.setAttribute('data-full-width-responsive', 'true');

        slot.appendChild(ins);

        // Push to adsbygoogle
        try {
            ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
        } catch (e) {
            console.error('AdSense push failed', e);
        }
    });
}

/**
 * Render all commodity categories as newspaper sections on one page.
 * If search is active, shows a single filtered section instead.
 */
async function renderNewspaperBody(
    latestDataMap: Record<string, LatestData>,
    trendDataMap: Record<string, TrendResponse>
) {
    const body = document.getElementById('newspaper-body');
    if (!body) return;
    body.innerHTML = '';

    // If searching, show filtered results in a single section
    if (searchTerm) {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = COMMODITIES.filter(c =>
            c.label.toLowerCase().includes(lowerSearch) ||
            (c.labelMm && c.labelMm.includes(searchTerm))
        );

        if (filtered.length === 0) {
            const emptyState = document.createElement('div');
            emptyState.className = 'empty-state';
            const message = document.createElement('p');
            message.textContent = `No results found for "${searchTerm}"`;
            const clearBtn = document.createElement('button');
            clearBtn.className = 'tab';
            clearBtn.textContent = 'Clear Search';
            clearBtn.addEventListener('click', () => {
                const input = document.getElementById('search-input') as HTMLInputElement;
                if (input) { input.value = ''; searchTerm = ''; renderApp(); }
            });
            emptyState.appendChild(message);
            emptyState.appendChild(clearBtn);
            body.appendChild(emptyState);
            return;
        }

        // Render search results as a single section
        const section = document.createElement('section');
        section.className = 'newspaper-section';
        section.innerHTML = `
            <div class="section-banner">
                <h2>Search Results</h2>
                <span class="section-desc">${filtered.length} items found</span>
            </div>
        `;
        const grid = document.createElement('div');
        grid.className = 'price-grid';

        renderCardsIntoGrid(grid, filtered, latestDataMap, trendDataMap);
        section.appendChild(grid);
        body.appendChild(section);

        renderSparklinesForCommodities(filtered, trendDataMap);
        initAddToListButtons();
        initFavoriteButtons();
        injectAds();
        return;
    }

    // Render all categories as newspaper sections
    let sectionIndex = 0;
    for (const [catKey, catMeta] of Object.entries(COMMODITY_CATEGORIES)) {
        const commodities = getCommoditiesByCategory(catKey);
        if (commodities.length === 0) continue;

        // Check if any commodity in this category has data
        const dataSource = catKey.startsWith('retail_') ? 'retail' : 'main';
        const latestData = latestDataMap[dataSource];
        if (!latestData) continue;

        const hasData = commodities.some(c => latestData.deltas[c.key]);
        if (!hasData) continue;

        // Get both English and Burmese labels
        const enLabel = catMeta.label;
        const mmLabel = (catMeta as { labelMm?: string }).labelMm || '';

        // Show the current language label as primary
        const primaryLabel = currentLanguage === 'mm' ? mmLabel || enLabel : enLabel;
        const secondaryLabel = currentLanguage === 'mm' ? enLabel : mmLabel;

        const section = document.createElement('section');
        section.className = 'newspaper-section';
        section.innerHTML = `
            <div class="section-banner">
                <h2>${primaryLabel}</h2>
                ${secondaryLabel ? `<span class="section-desc">${secondaryLabel}</span>` : ''}
            </div>
        `;

        const grid = document.createElement('div');
        grid.className = 'price-grid';

        renderCardsIntoGrid(grid, commodities, latestDataMap, trendDataMap);
        section.appendChild(grid);
        body.appendChild(section);

        // Insert an ad slot after every 3rd section
        sectionIndex++;
        if (sectionIndex % 3 === 0) {
            const adDiv = document.createElement('div');
            adDiv.className = 'ad-slot';
            adDiv.dataset.slotId = `SECTION_AD_${sectionIndex}`;
            adDiv.innerHTML = '<div class="ad-placeholder">Your advertisement could be here</div>';
            body.appendChild(adDiv);
        }
    }

    // After all sections rendered, render sparklines and init buttons
    const allCommodities = COMMODITIES;
    renderSparklinesForCommodities(allCommodities, trendDataMap);
    initAddToListButtons();
    initFavoriteButtons();
    injectAds();
}

/**
 * Helper: Render price cards into a grid element for a given list of commodities.
 */
function renderCardsIntoGrid(
    grid: HTMLElement,
    commodities: typeof COMMODITIES,
    latestDataMap: Record<string, LatestData>,
    trendDataMap: Record<string, TrendResponse>
) {
    commodities.forEach((commodity) => {
        const dataSource = commodity.category.startsWith('retail_') ? 'retail' : 'main';
        const latestData = latestDataMap[dataSource];
        const trendData = trendDataMap[dataSource];
        if (!latestData || !trendData) return;

        const delta = latestData.deltas[commodity.key];
        if (!delta) return;

        const sparklineValues = trendData.data.map(d => d[commodity.key]);

        const cardHTML = renderPriceCard({
            key: commodity.key,
            label: commodity.label,
            value: delta.value,
            unit: commodity.unit,
            source: commodity.category.startsWith('retail_') ? 'Citymall' : undefined,
            delta: {
                value: delta.value,
                change: delta.change,
                percentChange: delta.percentChange,
                trend: delta.trend,
            },
            sparklineData: sparklineValues as Array<number | null>,
            lang: currentLanguage,
            isFavorite: isFavorite(userPreferences, commodity.key),
        });

        const cardElement = document.createElement('div');
        cardElement.innerHTML = cardHTML;
        grid.appendChild(cardElement.firstElementChild!);
    });
}

/**
 * Helper: Render sparklines for a list of commodities after they are in the DOM.
 */
function renderSparklinesForCommodities(
    commodities: typeof COMMODITIES,
    trendDataMap: Record<string, TrendResponse>
) {
    commodities.forEach((commodity) => {
        const dataSource = commodity.category.startsWith('retail_') ? 'retail' : 'main';
        const trendData = trendDataMap[dataSource];
        if (!trendData) return;

        const sparklineValues = trendData.data.map(d => d[commodity.key]);
        const sparklineContainer = document.getElementById(`sparkline-${commodity.key}`);
        if (sparklineContainer && sparklineValues.some(v => v !== null)) {
            renderSparkline(sparklineContainer, {
                data: sparklineValues as Array<number | null>,
                width: sparklineContainer.offsetWidth,
                height: 28,
            });
        }
    });
}

function initAddToListButtons() {
    document.querySelectorAll('.add-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const target = e.currentTarget as HTMLElement;

            // Prevent double clicks
            if (target.classList.contains('added')) return;

            const key = target.dataset.key!;
            const label = target.dataset.label!;
            const unit = target.dataset.unit!;
            const price = parseFloat(target.dataset.price!);

            addToShoppingList({ key, label, unit, price, quantity: 1 });

            // Toast Feedback
            const addedText = t('addedToCart', currentLanguage); // or customized message
            showToast(`${target.dataset.label} added to ledger`);

            // Visual feedback on button (keep existing)
            target.classList.add('added');
            target.textContent = target.dataset.textAdded || '✔ Added'; // Use localized text from data attribute

            setTimeout(() => {
                target.classList.remove('added');
                target.textContent = target.dataset.textAdd || '+ Add to List';
            }, CONFIG.ADD_BUTTON_FEEDBACK_DURATION);
        });
    });
}

function initFavoriteButtons() {
    document.querySelectorAll('.fav-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevent card click
            const target = e.currentTarget as HTMLElement;
            const key = target.dataset.favKey!;

            // Toggle favorite
            if (isFavorite(userPreferences, key)) {
                userPreferences = removeFavorite(userPreferences, key);
                showToast('Removed from favorites', 'info');
            } else {
                userPreferences = addFavorite(userPreferences, key);
                showToast('Added to favorites!', 'success');
            }

            // Re-render to update UI
            renderApp();
        });
    });
}

function initSearchEvents() {
    const searchInput = document.getElementById('search-input') as HTMLInputElement;
    const searchClearBtn = document.getElementById('search-clear-btn');
    if (!searchInput) return;

    // Show/hide clear button based on input
    const updateClearButton = () => {
        if (searchClearBtn) {
            searchClearBtn.style.display = searchInput.value ? 'block' : 'none';
        }
    };

    // Clear button click handler
    if (searchClearBtn) {
        searchClearBtn.addEventListener('click', () => {
            searchInput.value = '';
            searchTerm = '';
            updateClearButton();
            renderApp();
        });
    }

    // Debounce search
    let timeout: ReturnType<typeof setTimeout>;
    searchInput.addEventListener('input', (e) => {
        const value = (e.target as HTMLInputElement).value.trim();
        updateClearButton();
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            searchTerm = value;
            renderApp();
        }, CONFIG.SEARCH_DEBOUNCE_MS);
    });
}

function addToShoppingList(item: ShoppingItem) {
    const existing = shoppingList.find(i => i.key === item.key);
    if (existing) {
        existing.quantity += 1;
    } else {
        shoppingList.push(item);
    }
    saveShoppingList(shoppingList);
    updateCartCount();
    renderCalculator();
}

function removeFromShoppingList(key: string) {
    shoppingList = shoppingList.filter(i => i.key !== key);
    saveShoppingList(shoppingList);
    updateCartCount();
    renderCalculator();
}

function updateItemQuantity(key: string, quantity: number) {
    const item = shoppingList.find(i => i.key === key);
    if (item) {
        item.quantity = quantity;
        saveShoppingList(shoppingList);
        updateCartCount();
        renderCalculator();
    }
}

function clearShoppingList() {
    shoppingList = [];
    saveShoppingList(shoppingList);
    updateCartCount();
    renderCalculator();
}

function toggleCalculator() {
    calculatorOpen = !calculatorOpen;
    renderCalculator();
}

function updateCartCount() {
    const badge = document.getElementById('cart-count');
    if (!badge) return;

    const count = shoppingList.reduce((acc, item) => acc + item.quantity, 0);
    badge.textContent = count.toString();

    if (count > 0) {
        badge.classList.remove('hidden');
    } else {
        badge.classList.add('hidden');
    }
}

function renderCalculator() {
    const container = document.getElementById('calculator-container');
    if (!container) return;

    // Toggle drawer overlay class
    if (!calculatorOpen) {
        container.classList.remove('open');
        container.innerHTML = '';
        return;
    }

    container.classList.add('open');

    container.innerHTML = renderShoppingCalculator(
        shoppingList,
        calculatorOpen,
        currentLanguage, // Pass language
        removeFromShoppingList,
        updateItemQuantity,
        clearShoppingList,
        toggleCalculator
    );

    initCalculatorEvents(
        container,
        removeFromShoppingList,
        updateItemQuantity,
        clearShoppingList,
        toggleCalculator
    );
}

function updateLastUpdated(timestamp: string | null | undefined) {
    if (!timestamp) return;
    const lastUpdatedElement = document.getElementById('last-updated');
    if (lastUpdatedElement) {
        const date = new Date(timestamp);
        lastUpdatedElement.textContent = `Updated: ${date.toLocaleString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        })}`;
    }
}

function initNavEvents() {
    const calcBtn = document.getElementById('nav-calc-btn');
    if (calcBtn) {
        calcBtn.onclick = toggleCalculator;
    }

    // Language Toggle
    const langBtn = document.getElementById('lang-toggle');
    if (langBtn) {
        langBtn.onclick = toggleLanguage;
        // Update button text
        // Update language toggle button
        const langSpan = document.getElementById('current-lang');
        if (langSpan) langSpan.textContent = ((window as any).currentLanguage as Language).toUpperCase();
    }
}

function toggleLanguage() {
    const newLang: Language = currentLanguage === 'en' ? 'mm' : 'en';
    (window as any).currentLanguage = newLang; // Update mutable copy
    localStorage.setItem('mp-lang', newLang);

    // Clear search on language toggle
    const searchInput = document.getElementById('search-input') as HTMLInputElement;
    if (searchInput) {
        searchInput.value = '';
        searchTerm = '';
    }

    renderApp();
}

/**
 * Update all elements with data-i18n attribute
 */
function updateStaticTranslations() {
    document.querySelectorAll('[data-i18n]').forEach(el => {
        const key = (el as HTMLElement).dataset.i18n;
        if (key && (key in (window as any))) return; // Avoid conflict? No.

        // We need to type check this or just cast
        // @ts-ignore
        const text = t(key, currentLanguage);
        if (text) el.textContent = text;
    });

    // Update specific elements that might not start with data-i18n or need complex handling
    const editionInfo = document.querySelector('.edition-info');
    if (editionInfo) editionInfo.textContent = t('editionInfo', currentLanguage);

    // Burmese title — always Burmese
    const paperTitleMm = document.querySelector('.paper-title-mm');
    if (paperTitleMm) paperTitleMm.textContent = t('paperTitleMm', currentLanguage);

    // English title
    const paperTitle = document.querySelector('.paper-title');
    if (paperTitle) paperTitle.textContent = t('paperTitle', currentLanguage);

    // Volume / Issue line
    const volumeLine = document.getElementById('volume-line');
    if (volumeLine) volumeLine.textContent = t('volumeLine', currentLanguage);

    // Date line
    const dateLine = document.getElementById('date-line');
    if (dateLine) {
        dateLine.textContent = formatNewspaperDate(new Date(), currentLanguage);
    }
}

async function renderApp() {
    updateCartCount();
    updateStaticTranslations();

    const newspaperBody = document.getElementById('newspaper-body');
    if (!newspaperBody) return;

    try {
        // Only show spinner if body is empty (initial load)
        if (!newspaperBody.children.length || newspaperBody.querySelector('.loading-state')) {
            newspaperBody.innerHTML = `
              <div class="loading-state">
                <div class="spinner"></div>
                <p>Fetching today's market data...</p>
              </div>
             `;
        }

        // Fetch data for BOTH main and retail endpoints
        const [mainLatest, mainTrend, retailLatest, retailTrend] = await Promise.all([
            fetchLatestData('essentials'),
            fetchTrendData('essentials', 7),
            fetchLatestData('retail_staples'),
            fetchTrendData('retail_staples', 7),
        ]);

        // Build data maps keyed by source type
        const latestDataMap: Record<string, LatestData> = {
            main: mainLatest,
            retail: retailLatest,
        };
        const trendDataMap: Record<string, TrendResponse> = {
            main: mainTrend,
            retail: retailTrend,
        };

        // Render quick stats from main data
        renderQuickStats(mainLatest);

        // Render all sections as newspaper body
        await renderNewspaperBody(latestDataMap, trendDataMap);

        // Update last updated timestamp
        if (mainLatest.today?.updated_at) {
            updateLastUpdated(mainLatest.today.updated_at);
        }

    } catch (error) {
        console.error('Failed to load data:', error);
        const errorMsg = getErrorMessage(error);
        const offline = !isOnline();

        newspaperBody.innerHTML = `
            <div class="loading-state">
                <p style="color: var(--trend-up)">${offline ? '🚫 Offline' : '⚠️ Error'}</p>
                <p style="margin-top: 0.5rem;">${errorMsg}</p>
                ${offline ? '<p style="font-size: 0.85rem; margin-top: 0.5rem; color: var(--ink-medium);">Waiting for connection...</p>' : ''}
                <button id="retry-btn" class="tab" style="margin-top: 1rem;">Try Again</button>
            </div>
        `;

        document.getElementById('retry-btn')?.addEventListener('click', () => {
            renderApp();
        });
    }
}

// Auto-refresh every 15 minutes
setInterval(renderApp, CONFIG.AUTO_REFRESH_INTERVAL);

// Initial render
document.addEventListener('DOMContentLoaded', () => {
    initNavEvents();
    initSearchEvents();
    renderApp();

    // Initialize PWA features
    registerServiceWorker();
    setupInstallPrompt();

    // Setup install button if app not already installed
    if (!isAppInstalled()) {
        const installBtn = document.getElementById('install-app-btn');
        if (installBtn) {
            installBtn.addEventListener('click', promptInstall);
        }
    }
});
